/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import EmailWithTooltip from "@stoneage/App/Components/Common/EmailWithTooltip";
import getMyAdminsData from "@stoneage/App/Handlers/MyAdmins/getMyAdminsData";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { styledSlider } from "@stoneage/XDL/Utilities";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// eslint-disable-next-line
import { A11y, Navigation, Scrollbar } from "swiper";
// eslint-disable-next-line
import { Swiper, SwiperSlide } from "swiper/react";

const enum fields {
    title = "title",
    titleStyle = "titleStyle",
    colorTheme = "colorTheme",
    domainsExcluded = "domainsExcluded",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titleStyle]: string;
        [fields.colorTheme]: "light" | "dark";
        [fields.domainsExcluded]: string;
    };
}

const mapStateToProps = (state: CustomAppState) => {
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const userName = state.context.session.userName;
    const billToState = getBillToState(state, state.context.session.billToId);
    let billTo;
    if (billToState && billToState.value) {
        billTo = billToState.value;
    }
    return {
        isAuthenticated: userName && !currentUserIsGuest,
        billTo,
    };
};

const mapDispatchToProps = {
    getMyAdminsData,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyAdminsStyles {
    widgetContainer?: InjectableCss;
    infoWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    infoItem?: TypographyPresentationProps;
}

export const myAdminsStyles: MyAdminsStyles = {
    widgetContainer: {
        css: css`
            height: 100%;
            padding: 20px;
            box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);
            border-radius: 2px;
            min-height: 260px;
            display: flex;
            flex-direction: column;

            .carousel-admins-container {
                ${styledSlider()};

                flex: 1;

                .swiper-container {
                    width: 100% !important;
                    padding: 0;
                }

                .swiper-slide {
                    & {
                        padding: 0;
                        min-height: 150px;
                    }
                    > div {
                        box-shadow: unset;
                    }
                    display: flex;
                    justify-content: flex-start;
                }

                .swiper-navigation .swiper-button-prev {
                    left: -20px;
                }

                .swiper-navigation .swiper-button-next {
                    right: -20px;
                }
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    infoItem: {
        css: css`
            display: block;
        `,
    },

    title: {
        css: css`
            margin-bottom: 12px;
        `,
    },
};

const MyAdmins = ({ id, fields, isAuthenticated, getMyAdminsData, billTo }: Props) => {
    const styles = myAdminsStyles;

    const [myAdminsInfo, setMyAdminsInfo] = React.useState([]);

    React.useEffect(() => {
        if (isAuthenticated) {
            getMyAdminsData({
                onSuccess: result => {
                    setMyAdminsInfo(result.accounts);
                },
                onError: () => {
                    setMyAdminsInfo([]);
                },
            });
        }
    }, []);

    React.useEffect(() => {
        const getStyles = async () => {
            // eslint-disable-next-line global-require
            const result = await require("swiper/swiper-bundle.min.css");
        };
        getStyles().then(result => {});
    }, []);

    if (!isAuthenticated) {
        return <></>;
    }

    const { title, titleStyle, colorTheme, domainsExcluded } = fields;
    const getTextColor = () => (colorTheme === "light" ? "common.black" : "common.white");

    if (!myAdminsInfo?.length) {
        return <></>;
    }

    if (domainsExcluded?.length) {
        // If the admins are all part of the execluded domains, dont display anything
        // make it lower case to acheive case insensitive comparison
        const domainsExcludedLowerCase = domainsExcluded.toLowerCase();

        const domainsExcludedAdmins = myAdminsInfo?.filter((admin: any) => {
            // get the domain of this admin and change it to lower case
            const myAdminDomain = admin.email.substring(admin.email.indexOf("@") + 1).toLowerCase();
            return domainsExcludedLowerCase.indexOf(myAdminDomain) > -1;
        });
        if (myAdminsInfo.length === domainsExcludedAdmins?.length) {
            return null;
        }
    }

    return (
        <StyledWrapper {...styles.widgetContainer}>
            {title && (
                <Typography {...styles.title} variant={titleStyle as keyof ThemeTypography} color={getTextColor()}>
                    {title}
                </Typography>
            )}

            <StyledWrapper className="carousel-admins-container">
                <Swiper
                    spaceBetween={0}
                    slidesPerView={1}
                    className="swiper-carousel"
                    modules={[Navigation, Scrollbar, A11y]}
                    wrapperClass="swiper-container"
                    navigation={{
                        nextEl: `.swiper-button-next.swiper-button-next${id}`,
                        prevEl: `.swiper-button-prev.swiper-button-prev${id}`,
                    }}
                    loop={false}
                >
                    {myAdminsInfo?.length &&
                        myAdminsInfo.map((admin: any, key: any) => {
                            const adminDomain = admin.email.substring(admin.email.indexOf("@") + 1);
                            if (admin.email && domainsExcluded.toLowerCase().indexOf(adminDomain.toLowerCase()) > -1) {
                                return null;
                            }
                            return (
                                <SwiperSlide key={`admin_${admin.id}`}>
                                    <StyledWrapper translate="no" {...styles.infoWrapper}>
                                        {(admin.firstName || admin.lastName) && (
                                            <Typography
                                                {...styles.infoItem}
                                                color={getTextColor()}
                                                css={css`
                                                    ${styles.infoItem?.css};
                                                    margin-bottom: 12px;
                                                `}
                                            >
                                                {admin.firstName}&nbsp;{admin.lastName}
                                            </Typography>
                                        )}
                                        {admin?.properties?.phone && (
                                            <Typography {...styles.infoItem} color={getTextColor()}>
                                                <a href={`tel:${admin?.properties?.phone}`}>
                                                    {admin?.properties?.phone}
                                                </a>
                                            </Typography>
                                        )}
                                        {admin.email && (
                                            <Typography
                                                {...styles.infoItem}
                                                color={getTextColor()}
                                                css={css`
                                                    ${styles.infoItem?.css};
                                                    font-size: 14px;
                                                `}
                                            >
                                                <EmailWithTooltip email={admin.email}></EmailWithTooltip>
                                            </Typography>
                                        )}
                                    </StyledWrapper>
                                </SwiperSlide>
                            );
                        })}
                </Swiper>
                <StyledWrapper className="swiper-navigation">
                    <div className={`swiper-button-prev swiper-button-prev${id}`}>
                        <ChevronLeft />
                    </div>
                    <div className={`swiper-button-next swiper-button-next${id}`}>
                        <ChevronRight />
                    </div>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const myAdmins: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyAdmins),
    definition: {
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.titleStyle,
                displayName: "Title Style",
                editorTemplate: "DropDownField",
                defaultValue: "h4",
                fieldType: "General",
                options: [
                    { displayName: "Heading 3", value: "h3" },
                    { displayName: "Heading 4", value: "h4" },
                    { displayName: "Heading 5", value: "h5" },
                    { displayName: "Heading 6", value: "h6" },
                ],
            },
            {
                name: fields.colorTheme,
                editorTemplate: "DropDownField",
                defaultValue: "light",
                fieldType: "General",
                options: [
                    { displayName: "Light", value: "light" },
                    { displayName: "Dark", value: "dark" },
                ],
            },
            {
                name: fields.domainsExcluded,
                displayName: "Hide Admins With These Domains (comma separated)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default myAdmins;
